import { h, render } from 'preact';
import { ChatApi } from './api';
import { readAnonId } from './pageContext';
import { buildStyles } from './styles';
import { Widget } from './ui/Widget';
import type { ConfigResponse } from './types';

// Single-file entry. Read <script data-site-key data-api-base> once, attach a
// Shadow Root to an injected host element, fetch site config, render Preact.
// Safe to load more than once — duplicate calls no-op.

declare global { interface Window { __VoyageChatMounted?: boolean; } }

(function boot() {
  if (window.__VoyageChatMounted) return;
  window.__VoyageChatMounted = true;

  const tag = document.currentScript as HTMLScriptElement | null;
  const siteKey = tag?.dataset.siteKey?.trim();
  const apiBase = (tag?.dataset.apiBase?.trim() || location.origin).replace(/\/+$/, '');

  if (!siteKey) {
    console.warn('[voyage-chat] data-site-key is required on the <script> tag; widget not mounted.');
    return;
  }

  const mount = () => {
    const host = document.createElement('div');
    host.id = 'voyage-chat-root';
    // Inline styles beat Shadow :host rules on the host element itself, so
    // positioning must be set here directly. `all: initial` would reset
    // position back to static, which is why we do NOT use it on the host.
    host.style.cssText = [
      'position: fixed',
      'right: 24px',
      'bottom: 24px',
      'left: auto',
      'top: auto',
      'width: auto',
      'height: auto',
      'margin: 0',
      'padding: 0',
      'border: 0',
      'z-index: 2147483000',
      'pointer-events: auto'
    ].join(';');
    document.body.appendChild(host);

    const shadow = host.attachShadow({ mode: 'closed' });

    const api = new ChatApi({ siteKey, apiBase, anonId: readAnonId() || undefined });

    api
      .getConfig()
      .then((config: ConfigResponse) => {
        const style = document.createElement('style');
        style.textContent = buildStyles(config.theme);
        shadow.appendChild(style);

        const mountPoint = document.createElement('div');
        shadow.appendChild(mountPoint);

        render(h(Widget, { config, api }), mountPoint);
      })
      .catch((err) => {
        console.warn('[voyage-chat] failed to load config; widget suppressed.', err);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
})();
